import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authState, toastsState } from '../state/atoms';
import { categoriesColors, classNames } from '../utils/formatters';

import { API } from 'aws-amplify';
import { useRecoilState } from 'recoil';
import AdminControlPanel from '../components/Panels/AdminControlPanel';
import AdminUnlockPanel from '../components/Panels/AdminUnlockPanel';
import ApplicationStatusPanel from '../components/Panels/ApplicationStatusPanel';
import DeficiencyReviewPanel from '../components/Panels/DeficiencyReviewPanel';
import UpdateRecordPanel from '../components/Panels/UpdateRecordPanel';
import WorkflowHistoryPanel from '../components/Panels/WorkflowHistoryPanel';
import SkeletonTable from '../components/SkeletonTable';

// show a list of submissions for the selected fund
const Submission = (props: any): JSX.Element => {
  const navigate = useNavigate();
  const [toasts, setToasts]: [any, any] = useRecoilState(toastsState);
  const [auth]: any = useRecoilState(authState);

  const userEmail = auth && auth.attributes && auth.attributes.email;
  const { fundId, submissionId } = useParams<{
    fundId: string;
    submissionId: string;
  }>();

  const [loading, setLoading] = useState(true);
  const [application, setApplication]: [any, any] = useState();
  const answerFormatter = (row: any) => {
    // console.log("answerFormatter", row.key, row.answer)

    if (row.isUpload) {
      // render image or collection of links
      if (row.key.indexOf('signature') > -1) {
        return (
          <img
            style={{ maxWidth: '100%' }}
            src={row.answer}
            alt=""
          />
        );
      } else if (row.answer.indexOf('<br/>') > -1) {
        return row.answer.replace('<br/>', ', ');
      } else {
        return (
          <ul>
            {row.answer.map((url: string, index: number) => {
              let fileName = url.split('?')[0];
              let fileNameParts = fileName.split('/');
              fileName = fileNameParts[fileNameParts.length - 1];
              return (
                <li key={index}>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    className="text-green-600 underline hover:no-underline"
                    href={url.replace('?apikey=', '?apiKey=')}
                  >
                    {fileName}
                  </a>
                </li>
              );
            })}
          </ul>
        );
      }
    } else if (row.questionText === 'Benefit Application Type') {
      return (
        <span
          className={classNames(
            categoriesColors[row.answer && row.answer.split(' - ')[0]],
            'px-2 py-1 inline-flex text-sm leading-5 font-semibold rounded',
          )}
        >
          {row.answer}
        </span>
      );
    } else if (
      row.answer &&
      typeof row.answer === 'object' &&
      row.answer['other'] !== undefined
    ) {
      let retval = '';
      for (const key in row.answer) {
        retval += row.answer[key] + ', ';
      }
      return retval.substring(0, retval.length - 2);
    } else if (Array.isArray(row.answer)) {
      return row.answer.join(', ');
    }

    return row.answer;
  };

  // parse the response from the API for display on the page
  const processResponse = useCallback(
    (applicationResponse: any) => {
      // handle redirect on case of delete
      if (applicationResponse.redirect) {
        navigate(applicationResponse.redirect);
        return;
      }

      // handle error
      console.log('applicationResponse', applicationResponse);

      applicationResponse.steps = JSON.parse(applicationResponse.steps);

      setApplication(applicationResponse);
      setLoading(false);
    },
    [navigate],
  );

  // handle actual processing of an action
  const handleAction = useCallback(
    async (action, note?: string, newStep?: any) => {
      try {
        setLoading(true);
        console.log('processAction', action, note, newStep);
        const applicationResponse = await API.post(
          'submissions',
          `/submissions`,
          {
            body: {
              user: userEmail,
              formId: fundId,
              submissionId: submissionId,
              action: action,
              notes: note,
              step:
                typeof newStep === 'number' && newStep >= 0
                  ? newStep + 1
                  : application.step,
            },
          },
        );
        processResponse(applicationResponse);
        setToasts([
          ...toasts,
          {
            type: 'success',
            title: 'Action Successful',
            description: 'Your update has been processed successfully.',
            timeout: 5000,
          },
        ]);
      } catch (e) {
        console.log('An error occurred');
        setToasts([
          ...toasts,
          {
            type: 'error',
            title: 'An Error Occurred',
            description:
              'Something went wrong and we could not complete your request. Please try again later.',
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [
      userEmail,
      fundId,
      submissionId,
      application,
      processResponse,
      setToasts,
      toasts,
    ],
  );

  const copyEditLink = () => {
    navigator.clipboard.writeText(
      'https://www.jotform.com/edit/' + submissionId,
    );
  };

  // load initial data
  useEffect(() => {
    (async () => {
      setLoading(true);
      const applicationResponse = await API.get('submissions', `/submissions`, {
        queryStringParameters: {
          fundId: fundId,
          submissionId: submissionId,
        },
      });
      console.log('applicationResponse', applicationResponse);
      processResponse(applicationResponse);
      setLoading(false);
    })();
  }, [fundId, submissionId, processResponse]);

  return (
    <div className="min-h-full">
      <div className="pb-24">
        <header className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              {application ? (
                application['applicantName']
              ) : (
                <div className="h-3 w-60 mx-auto animate-pulse rounded bg-gray-300"></div>
              )}
            </h1>
            <div className="text-white text-xl mb-2 flex align-middle">
              <span>Submission#{submissionId}</span>
              <button
                type="button"
                onClick={copyEditLink}
                title="Copy Link to Edit Submission"
                className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white  hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
            <button
              onClick={() => {
                navigate('/funds/' + fundId);
              }}
              className="text-green-400 underline hover:no-underline text-l mb-4"
            >
              {application ? (
                <Link to={`/funds/${fundId}`}>
                  {application ? application!['fundName'] : 'Fund'}
                </Link>
              ) : (
                <div className="h-3 w-60 animate-pulse rounded bg-gray-300"></div>
              )}
            </button>
          </div>
        </header>
      </div>
      {loading || !application ? (
        <SkeletonTable />
      ) : (
        <>
          <main className="-mt-32 pb-48">
            <div className="max-w-7xl mx-auto pb-12 px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col">
                <div className="-my-2 sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden sm:rounded-lg">
                      <div className="h-full flex flex-col md:flex-row">
                        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none xl:order-first mb-6">
                          <h2 className="text-2xl font-bold text-white mb-4">
                            Application Data
                          </h2>
                          <div className="inset-0">
                            <div className="h-full border-2 md:mr-8 border-gray-200 shadow rounded-lg bg-white">
                              <table className="table-fixed divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th
                                      scope="col"
                                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                      Question
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                      Answer
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {application &&
                                    application.formattedResponse &&
                                    application.formattedResponse
                                      .filter(
                                        (row: any) =>
                                          row.answer &&
                                          row.answer !== '' &&
                                          row.answer !== ' ' &&
                                          row.answer !== 'I acknowledge',
                                      )
                                      .map((row: any, i: number) => (
                                        <tr
                                          key={i}
                                          className={
                                            i % 2 === 1 ? 'bg-gray-50' : ''
                                          }
                                        >
                                          <td className="px-6 py-4 w-1/3 text-sm font-medium text-gray-900 align-top">
                                            {row.questionText}
                                          </td>
                                          <td className="px-6 py-4 w-2/3 text-sm text-gray-500 align-top">
                                            {answerFormatter(row)}
                                          </td>
                                        </tr>
                                      ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </main>
                        <aside className="relative xl:order-last xl:flex xl:flex-col flex-shrink-0 md:w-96 overflow-y-auto">
                          <h2 className="text-2xl font-bold text-white mb-4">
                            Approval Information
                          </h2>

                          {/* Application Status */}
                          <ApplicationStatusPanel
                            handleAction={handleAction}
                            submission={application}
                          />

                          {/* Update Record */}
                          {application &&
                          application.status === 'In Progress' ? (
                            <UpdateRecordPanel
                              steps={application.steps}
                              step={application.step}
                              handleAction={handleAction}
                              assignedTo={application.assignedTo}
                              instructions={
                                application.steps[application.step - 1]
                                  .instructions
                              }
                            />
                          ) : null}

                          {/* Deficiency Review */}
                          {application &&
                          application.status === 'Deficiency Review' &&
                          application.admins.includes(userEmail) ? (
                            <DeficiencyReviewPanel
                              handleAction={handleAction}
                              steps={application.steps}
                              step={application.step}
                            />
                          ) : null}

                          {/* Admin Unlock Controls */}
                          {application &&
                          application.admins.includes(userEmail) &&
                          application.status !== 'Rejected' &&
                          application.status !== 'Approved' ? (
                            <AdminControlPanel handleAction={handleAction} />
                          ) : null}

                          {/* Admin Unlock Controls */}
                          {application &&
                          application.admins.includes(userEmail) &&
                          (application.status === 'Rejected' ||
                            application.status === 'Approved' ||
                            application.status === 'Conditional Approval') ? (
                            <AdminUnlockPanel handleAction={handleAction} />
                          ) : null}

                          {/* Workflow history */}
                          <WorkflowHistoryPanel
                            historyItems={application && application.notes}
                          />
                        </aside>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </>
      )}
    </div>
  );
};

export default Submission;
